<template>
  <div class="bg-white border rounded mb-2 px-3 py-2" :style="bgColor">
    <b-row>
      <b-col
        cols="12"
        md="6"
        class="d-flex justify-content-between align-items-center pr-md-1"
      >
        <div>
          <router-link
            :to="{ name: 'SearchByMember', params: { user: post.user } }"
            ><strong>{{ post.userName }}</strong></router-link
          >
          <b-badge
            v-if="this.post.categoryId && category"
            class="ml-2"
            variant="secondary"
            pill
            ><router-link class="text-light" :to="category.route">{{
              category.name
            }}</router-link></b-badge
          >
        </div>
        <div class="text-right d-md-none">
          <b-dropdown
            toggle-class="text-decoration-none"
            no-caret
            variant="link"
            boundary="viewport"
            size="sm"
          >
            <template #button-content>
              <span title="Post actions"
                ><b-icon-three-dots-vertical variant="secondary"
              /></span>
            </template>
            <b-dropdown-item v-if="post.canManage" :to="editTargetRoute"
              >Edit</b-dropdown-item
            >
            <b-dropdown-item
              v-if="post.canManage"
              @click="onDelete"
              variant="danger"
              >Delete</b-dropdown-item
            >
            <b-dropdown-item :to="permalink">Permalink</b-dropdown-item>
          </b-dropdown>
        </div>
      </b-col>
      <b-col cols="12" md="6" class="text-md-right pl-md-1">
        <span v-if="post.canManage" class="d-none d-md-inline">
          <small><router-link :to="editTargetRoute">Edit</router-link></small>
          <small> | </small>
          <small
            ><a href="#" @click.prevent="onDelete" class="text-danger"
              >Delete</a
            ></small
          >
          <small> | </small>
        </span>
        <span class="d-none d-md-inline">
          <small
            ><router-link :to="permalink" class="text-info"
              >Permalink</router-link
            ></small
          >
          <small> ~ </small>
        </span>
        <small class="text-muted">{{ timestamp }}</small></b-col
      >
      <b-col cols="12" :class="{ 'post--collapsed': collapsed }">
        <div v-html="formattedMessage" class="post__message"></div>
        <div v-show="collapsed" class="post__expander">
          <a href="#" @click.prevent="onExpand" :class="expanderVariant"
            ><b-icon icon="chevron-double-down" font-scale="0.75" /> show full
            post <b-icon icon="chevron-double-down" font-scale="0.75"
          /></a>
        </div>
        <div v-show="!collapsed && collapsable" class="post__collapser">
          <a href="#" @click.prevent="onCollapse"
            ><b-icon icon="chevron-double-up" font-scale="0.75" /> hide full
            post <b-icon icon="chevron-double-up" font-scale="0.75"
          /></a>
        </div>
      </b-col>
      <b-col v-if="post.imageUrl" cols="12" md="2" class="pt-2">
        <a :href="post.imageUrl" target="_blank">
          <b-img fluid-grow rounded :src="resizedImageUrl" alt=""
        /></a>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import format from "date-fns/format";
import formatPost from "@/formatters/post.formatter";
import postCategory from "@/services/postCategory";

export default {
  name: "Post",
  props: ["keepExpanded", "post"],
  data: () => ({
    collapsed: true,
  }),
  computed: {
    bgColor() {
      return {
        backgroundColor: `${this.post.bgColor} !important`,
      };
    },
    category() {
      return postCategory.getById(this.post.categoryId);
    },
    collapsable() {
      return this.post.message.length > 700 && !this.keepExpanded;
    },
    editTargetRoute() {
      return { name: "MessageEdit", params: { id: this.post.id } };
    },
    expanderVariant() {
      if (this.post.userName === "RevShark") return "expander--charlotte";
      if (this.post.userName === "RevSharkTweets") return "expander--gallery";
      if (this.post.userName === "Jim") return "expander--foam";
      return "";
    },
    formattedMessage() {
      return formatPost(this.post.message);
    },
    permalink() {
      return { name: "MessageView", params: { id: this.post.id } };
    },
    resizedImageUrl() {
      // ImageResizer module resizes moderator images
      if (/^\/media/i.test(this.post.imageUrl)) {
        return `${this.post.imageUrl}?width=480`;
      }
      return this.post.imageUrl;
    },
    timestamp() {
      return format(
        new Date(this.post.timestamp),
        "EEE, MMM d, yyyy h:mm:ss aa"
      );
    },
  },
  methods: {
    onCollapse() {
      this.collapsed = true;
    },
    onDelete() {
      this.$emit("destroy", this.post.id);
    },
    onExpand() {
      this.collapsed = false;
    },
  },
  mounted() {
    this.collapsed = this.collapsable;
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_colors";

.post__message {
  overflow-wrap: break-word;

  p {
    margin-bottom: 0;
  }
}

.post--collapsed {
  max-height: 190px;
  overflow: hidden;
  position: relative;
}

.post__collapser {
  a {
    display: block;
    padding: 1vh;
    text-align: center;
  }
}

.post__expander {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;

  a {
    background: linear-gradient(
      transparentize($si-color-white, 1),
      transparentize($si-color-white, 0.1) 50%
    );
    display: block;
    padding: 8vh 1vh 1vh;
    text-align: center;

    &:hover {
      background: linear-gradient(
        transparentize($si-color-white, 1),
        transparentize($si-color-white, 0.15) 70%
      );
    }

    &.expander--charlotte {
      background: linear-gradient(
        transparentize($si-color-charlotte, 1),
        transparentize($si-color-charlotte, 0.1) 50%
      );

      &:hover {
        background: linear-gradient(
          transparentize($si-color-charlotte, 1),
          transparentize($si-color-charlotte, 0.15) 70%
        );
      }
    }

    &.expander--foam {
      background: linear-gradient(
        transparentize($si-color-foam, 1),
        transparentize($si-color-foam, 0.1) 50%
      );

      &:hover {
        background: linear-gradient(
          transparentize($si-color-foam, 1),
          transparentize($si-color-foam, 0.15) 70%
        );
      }
    }

    &.expander--gallery {
      background: linear-gradient(
        transparentize($si-color-gallery, 1),
        transparentize($si-color-gallery, 0.1) 50%
      );

      &:hover {
        background: linear-gradient(
          transparentize($si-color-gallery, 1),
          transparentize($si-color-gallery, 0.15) 70%
        );
      }
    }
  }
}
</style>
